import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { LogOut, Users, Shield } from "lucide-react";
import logoHorizontal from "@/assets/logo-horizontal.png";

interface Profile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string | null;
  profile_type: string;
  created_at: string;
}

const profileLabels: Record<string, string> = {
  profissional: "Profissional sob estresse",
  "pai-tutor": "Pai / Tutor",
  "cuidador-idoso": "Cuidador de idoso",
};

const Admin = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      if (!roles?.some(r => r.role === "admin")) {
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);

      const { data } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      setProfiles((data as Profile[]) || []);
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (!isAdmin || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jade-light font-body">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoHorizontal} alt="MentalSync" className="h-8" />
            <div className="flex items-center gap-1.5 bg-gold/10 text-gold px-2 py-1 rounded text-xs font-semibold">
              <Shield className="h-3 w-3" /> Admin
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-1" /> Sair
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-6 w-6 text-gold" />
          <h1 className="font-display text-2xl font-bold text-foreground">
            Usuários Cadastrados ({profiles.length})
          </h1>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-semibold text-foreground">Nome</th>
                  <th className="text-left p-4 font-semibold text-foreground">E-mail</th>
                  <th className="text-left p-4 font-semibold text-foreground">Telefone</th>
                  <th className="text-left p-4 font-semibold text-foreground">Perfil</th>
                  <th className="text-left p-4 font-semibold text-foreground">Data</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(profile => (
                  <tr key={profile.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-medium text-foreground">{profile.name || "—"}</td>
                    <td className="p-4 text-muted-foreground">{profile.email}</td>
                    <td className="p-4 text-muted-foreground">{profile.phone || "—"}</td>
                    <td className="p-4">
                      <span className="inline-block bg-jade-light text-foreground text-xs font-medium px-2 py-1 rounded">
                        {profileLabels[profile.profile_type] || profile.profile_type}
                      </span>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {new Date(profile.created_at).toLocaleDateString("pt-BR")}
                    </td>
                  </tr>
                ))}
                {profiles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      Nenhum usuário cadastrado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
